import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

interface ThinkingIndicatorProps {
  label: string;
  /** Optional secondary reassurance line, shown after a longer wait. */
  detail?: string;
}

/**
 * Assistant-styled "working" indicator shown while a response is being generated
 * but no streamed content has arrived yet (isStreaming && !streamingContent).
 * Mirrors StreamingMessage's avatar/bubble layout so the transition to streamed
 * text is seamless.
 */
export function ThinkingIndicator({ label, detail }: ThinkingIndicatorProps) {
  return (
    <Box sx={{ mb: 2, px: 2 }} aria-live="polite">
      {/* Avatar + name row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "primary.main",
            fontSize: "0.85rem",
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary"
          }}>
          Sales Coach
        </Typography>
      </Box>
      {/* Indicator bubble */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          maxWidth: "75%",
          bgcolor: "grey.100",
          borderRadius: 2,
          ml: 4.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            {label}
          </Typography>
          {/* Animated dots */}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              gap: 0.5,
              "& > span": {
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "text.secondary",
                animation: "thinkingPulse 1.2s ease-in-out infinite",
              },
              "& > span:nth-of-type(2)": { animationDelay: "0.2s" },
              "& > span:nth-of-type(3)": { animationDelay: "0.4s" },
              "@keyframes thinkingPulse": {
                "0%, 80%, 100%": { opacity: 0.3 },
                "40%": { opacity: 1 },
              },
            }}
          >
            <span />
            <span />
            <span />
          </Box>
        </Box>
        {detail && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 0.75,
              display: "block"
            }}>
            {detail}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
